import { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { 
  User, Mail, Phone, MapPin, Edit, Save, X,
  Calendar, Shield, Loader
} from 'lucide-react';
import { API_URL } from '../Config';

export default function Profile() {
  const [user, setUser] = useState(null);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [message, setMessage] = useState({ text: '', type: '' });
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/profile`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(res.data.data);
      setForm(res.data.data);
    } catch (err) {
      if (err.response?.status === 401) {
        navigate('/login');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    setUpdating(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.put(`${API_URL}/profile`, form, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(res.data.data);
      setMessage({ 
        text: 'Profil mis à jour avec succès', 
        type: 'success' 
      });
      setEditing(false);
      setTimeout(() => setMessage({ text: '', type: '' }), 3000);
    } catch (err) {
      setMessage({ 
        text: err.response?.data?.message || 'Erreur lors de la mise à jour', 
        type: 'error' 
      });
      setTimeout(() => setMessage({ text: '', type: '' }), 3000);
    } finally {
      setUpdating(false);
    }
  };

  const getRoleColor = (role) => {
    const colors = {
      'admin': 'bg-red-100 text-red-700 border-red-200',
      'client': 'bg-blue-100 text-blue-700 border-blue-200',
      'validateur': 'bg-green-100 text-green-700 border-green-200',
      'chef_equipe': 'bg-purple-100 text-purple-700 border-purple-200',
      'magasinier': 'bg-orange-100 text-orange-700 border-orange-200',
      'mecanicien': 'bg-cyan-100 text-cyan-700 border-cyan-200'
    };
    return colors[role?.toLowerCase()] || 'bg-gray-100 text-gray-700 border-gray-200';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <Loader className="w-8 h-8 text-gray-400 animate-spin mx-auto" />
          <p className="mt-3 text-gray-500 text-sm">Chargement...</p>
        </div>
      </div>
    );
  }

  if (!user) return null;

  // Extraire les informations de l'utilisateur
  const userNom = user.nom || 'Utilisateur';
  const userRole = user.role || 'client';
  const userInitial = userNom.charAt(0).toUpperCase();

  return (
    <div className="min-h-screen">
      {/* Header */}
      <div className="border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 py-6">
          <h1 className="text-2xl font-semibold text-gray-900">Bonjour, {userNom.toUpperCase()}</h1>
          <p className="text-gray-500 text-sm mt-1">Gérer vos informations personnelles</p>
        </div>
      </div>

      <div className="max-w-5xl mx-auto  py-8">
        {/* Notification */}
        {message.text && (
          <div className={`mb-6 p-3 rounded-md ${message.type === 'success' ? 'bg-green-50' : 'bg-red-50'}`}>
            <div className="flex items-center">
              <div className={`mr-2 ${message.type === 'success' ? 'text-green-500' : 'text-red-500'}`}>
                {message.type === 'success' ? (
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                ) : (
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                )}
              </div>
              <span className={`text-sm ${message.type === 'success' ? 'text-green-700' : 'text-red-700'}`}>
                {message.text}
              </span>
            </div>
          </div>
        )}

        <div className="grid md:grid-cols-3 gap-6">
          {/* Profile Card */}
          <div className="md:col-span-1">
            <div className="bg-white border border-gray-200 rounded-lg p-5 shadow-sm">
              {/* Avatar */}
              <div className="flex flex-col items-center mb-6">
                <div className="w-20 h-20 rounded-full bg-gray-100 flex items-center justify-center mb-3 border border-gray-200">
                  {user.photo ? (
                    <img 
                      src={user.photo} 
                      alt={userNom} 
                      className="w-full h-full rounded-full object-cover"
                    />
                  ) : (
                    <div className={`w-full h-full rounded-full flex items-center justify-center font-semibold text-xl ${getRoleColor(userRole)}`}>
                      {userInitial}
                    </div>
                  )}
                </div>
                <h2 className="text-lg font-medium text-gray-900">{userNom}</h2>
                <div className={`px-3 py-1 rounded-full text-xs font-medium mt-2 border ${getRoleColor(userRole)}`}>
                  <span className="capitalize">{userRole.replace('_', ' ')}</span>
                </div>
              </div>

              {/* Contact Info */}
              <div className="space-y-4">
                <div>
                  <div className="flex items-center text-gray-500 mb-1">
                    <Mail className="w-3 h-3 mr-2" />
                    <span className="text-xs">Email</span>
                  </div>
                  <p className="text-sm text-gray-900 truncate">{user.email}</p>
                </div>

                {user.phone && (
                  <div>
                    <div className="flex items-center text-gray-500 mb-1">
                      <Phone className="w-3 h-3 mr-2" />
                      <span className="text-xs">Téléphone</span>
                    </div>
                    <p className="text-sm text-gray-900">{user.phone}</p>
                  </div>
                )}

                {user.ville && (
                  <div>
                    <div className="flex items-center text-gray-500 mb-1">
                      <MapPin className="w-3 h-3 mr-2" />
                      <span className="text-xs">Ville</span>
                    </div>
                    <p className="text-sm text-gray-900">{user.ville}</p>
                  </div>
                )}

                <div className="pt-4 border-t border-gray-100">
                  <div className="flex items-center text-gray-500 mb-1">
                    <Calendar className="w-3 h-3 mr-2" />
                    <span className="text-xs">Membre depuis</span>
                  </div>
                  <p className="text-sm text-gray-900">
                    {new Date(user.created_at).toLocaleDateString('fr-FR', {
                      day: 'numeric',
                      month: 'short',
                      year: 'numeric'
                    })}
                  </p>
                </div>
                <div className="pt-4 border-t border-gray-100">
                  <button onClick={() => navigate('/change-password')} className="flex items-center text-gray-900 mb-1">
                      <Edit className="w-3 h-3 mr-2" />
                      <span className="text-xs" >Modifier le mot de passe</span>
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Form Section */}
          <div className="md:col-span-2">
            <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
              {/* Form Header */}
              <div className="border-b border-gray-200 px-5 py-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-medium text-gray-900">Informations personnelles</h3>
                    <p className="text-gray-500 text-sm mt-1">
                      {editing ? 'Modifier vos informations' : 'Visualiser vos informations'}
                    </p>
                  </div>
                  {!editing && (
                    <button
                      onClick={() => setEditing(true)}
                      className="inline-flex items-center px-3 py-1.5 text-sm border border-gray-300 rounded-md text-gray-700 hover: transition-colors"
                    >
                      <Edit className="w-3 h-3 mr-1.5" />
                      Modifier
                    </button>
                  )}
                </div>
              </div>

              <form onSubmit={handleUpdate}>
                <div className="p-5">
                  {/* Basic Info */}
                  <div className="mb-6">
                    <h4 className="text-sm font-medium text-gray-700 mb-3">Informations de base</h4>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm text-gray-600 mb-1">Nom complet</label>
                        <input
                          type="text"
                          value={editing ? form.nom : userNom}
                          onChange={(e) => setForm({ ...form, nom: e.target.value })}
                          disabled={!editing}
                          className={`w-full px-3 py-2 text-sm border rounded transition-colors ${editing ? 'border-gray-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-500' : 'border-gray-200  text-gray-500'}`}
                          required
                        />
                      </div>

                      <div>
                        <label className="block text-sm text-gray-600 mb-1">Email</label>
                        <div className="relative">
                          <input
                            type="email"
                            value={user.email}
                            disabled
                            className="w-full px-3 py-2 text-sm border border-gray-200  rounded text-gray-500"
                          />
                          <Mail className="absolute right-3 top-2 w-3 h-3 text-gray-400" />
                        </div>
                        <p className="text-xs text-gray-400 mt-1">Email non modifiable</p>
                      </div>
                    </div>
                  </div>

                  {/* Contact Info */}
                  <div className="mb-6">
                    <h4 className="text-sm font-medium text-gray-700 mb-3">Contact</h4>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm text-gray-600 mb-1">Téléphone</label>
                        <input
                          type="tel"
                          value={editing ? (form.phone || '') : (user.phone || 'Non renseigné')}
                          onChange={(e) => setForm({ ...form, phone: e.target.value })}
                          disabled={!editing}
                          className={`w-full px-3 py-2 text-sm border rounded transition-colors ${editing ? 'border-gray-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-500' : 'border-gray-200  text-gray-500'}`}
                        />
                      </div>

                      <div>
                        <label className="block text-sm text-gray-600 mb-1">Ville</label>
                        <input
                          type="text"
                          value={editing ? (form.ville || '') : (user.ville || 'Non renseignée')}
                          onChange={(e) => setForm({ ...form, ville: e.target.value })}
                          disabled={!editing}
                          className={`w-full px-3 py-2 text-sm border rounded transition-colors ${editing ? 'border-gray-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-500' : 'border-gray-200  text-gray-500'}`}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  {editing && (
                    <div className="flex items-center justify-end space-x-3 pt-4 border-t border-gray-200">
                      <button
                        type="button"
                        onClick={() => { setEditing(false); setForm(user); }}
                        className="px-4 py-2 text-sm border border-gray-300 rounded text-gray-700 hover: transition-colors"
                      >
                        <div className="flex items-center">
                          <X className="w-3 h-3 mr-1.5" />
                          Annuler
                        </div>
                      </button>
                      <button
                        type="submit"
                        disabled={updating}
                        className="px-4 py-2 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 transition-colors"
                      >
                        <div className="flex items-center">
                          {updating ? (
                            <>
                              <Loader className="w-3 h-3 mr-1.5 animate-spin" />
                              Enregistrement...
                            </>
                          ) : (
                            <>
                              <Save className="w-3 h-3 mr-1.5" />
                              Enregistrer
                            </>
                          )}
                        </div>
                      </button>
                    </div>
                  )}
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}